// The Learn card's words, decided from the puzzle's motif and the reader's
// record: the title, the line that counts the reader's mistakes of the same
// family in the last 30 days, and where that line opens in the library.
// Pure, so they are tested without React; the container in `index.tsx`
// passes them to `LearnCard`.
import type { MoveKind } from '@chess/api-model';
import { mistakeKey, motifLabel, type Motif, type MotifFamily } from '@chess/api-mistakes';

/** "Learn: Pin · against the queen". */
export function learnTitle(motif: Motif): string {
  return `Learn: ${motifLabel(motif)}`;
}

/** Each family as the count line names it after "were": "a pin", "positional". */
const FAMILY_PHRASE: Readonly<Record<MotifFamily, string>> = Object.freeze({
  pin: 'a pin',
  skewer: 'a skewer',
  fork: 'a fork',
  discoveredAttack: 'a discovered attack',
  hangingPiece: 'a hanging piece',
  trappedPiece: 'a trapped piece',
  captureDefender: 'a defender captured',
  mate: 'a mate',
  pawn: 'a pawn theme',
  kingAttack: 'a king attack',
  combination: 'a combination',
  positional: 'positional',
});

export function familyPhrase(family: MotifFamily): string {
  return FAMILY_PHRASE[family];
}

/** The library filtered to the reader's mistakes of the kind and family: `/library?mistake=blunder-motif-pin`. */
export function libraryHref(kind: MoveKind, family: MotifFamily): string {
  return `/library?mistake=${encodeURIComponent(mistakeKey(kind, 'motif', family))}`;
}

export interface CountLine {
  /** "3 of your blunders in the last 30 days were a pin". */
  text: string;
  href: string;
}

/**
 * The line counting the reader's mistakes of the puzzle's kind and family
 * over the period; none when the count is 0 or not known yet (the store
 * still loading, the aggregator not run).
 */
export function countLine(kind: MoveKind, family: MotifFamily, count: number | undefined): CountLine | undefined {
  if (!count) return undefined;
  // "1 of your misses was": the set stays plural, the verb agrees with the count.
  const what = kind === 'blunder' ? 'blunders' : 'misses';
  const verb = count === 1 ? 'was' : 'were';
  return { text: `${count} of your ${what} in the last 30 days ${verb} ${familyPhrase(family)}`, href: libraryHref(kind, family) };
}
